import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  DatePicker,
  Space,
  Table,
  Tag,
  Select,
  Typography,
  Button,
  Spin,
  Avatar,
  Statistic,
  Tooltip,
  Badge,
  Grid,
} from "antd";
import {
  PhoneOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import api from "../api/Api";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const GestaoChamadasAgentes = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;

  const [datas, setDatas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dadosAgentes, setDadosAgentes] = useState([]);
  const [chamadas, setChamadas] = useState([]);
  const [agenteSelecionado, setAgenteSelecionado] = useState(null);
  const [dadosGrafico, setDadosGrafico] = useState([]);
  const [loadingDados, setLoadingDados] = useState(false);

  const fetchDadosAgentes = async () => {
    if (!datas || !datas[0] || !datas[1]) return;
    try {
      setLoadingDados(true);
      const payload = {
        dataInicio: datas[0].format("YYYY-MM-DD"),
        dataFim: datas[1].format("YYYY-MM-DD"),
      };
      const response = await api.ChamadaAgente(payload);
      if (Array.isArray(response)) setDadosAgentes(response);
    } catch (error) {
      console.error("Erro ao buscar dados dos agentes:", error);
    } finally {
      setLoadingDados(false);
    }
  };

  const fetchChamadasAgentes = async () => {
    if (!datas || !datas[0] || !datas[1]) return;
    try {
      setLoading(true);
      const payload = {
        dataInicio: datas[0].format("YYYY-MM-DD"),
        dataFim: datas[1].format("YYYY-MM-DD"),
      };
      const response = await api.ListRegistoAgente(payload);
      if (Array.isArray(response)) setChamadas(response);
    } catch (error) {
      console.error("Erro ao buscar chamadas dos agentes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (datas && datas[0] && datas[1]) {
      fetchDadosAgentes();
      fetchChamadasAgentes();
      setAgenteSelecionado(null);
      setDadosGrafico([]);
    }
  }, [datas]);

  useEffect(() => {
    if (agenteSelecionado && chamadas.length > 0) {
      const chamadasAgente = chamadas.filter((c) => c.agente === agenteSelecionado);
      const chamadasPorDia = {};
      chamadasAgente.forEach((chamada) => {
        const data = dayjs(chamada.data).format("DD/MM/YYYY");
        if (!chamadasPorDia[data]) chamadasPorDia[data] = 0;
        chamadasPorDia[data]++;
      });
      const dadosGraficoArray = Object.keys(chamadasPorDia)
        .map((data) => ({ data, chamadas: chamadasPorDia[data] }))
        .sort((a, b) => dayjs(a.data, "DD/MM/YYYY").valueOf() - dayjs(b.data, "DD/MM/YYYY").valueOf());
      setDadosGrafico(dadosGraficoArray);
    } else {
      setDadosGrafico([]);
    }
  }, [agenteSelecionado, chamadas]);

  const agenteDetalhes = useMemo(() => {
    if (!agenteSelecionado) return null;
    return dadosAgentes.find((a) => a.agente === agenteSelecionado);
  }, [agenteSelecionado, dadosAgentes]);

  const filteredChamadas = useMemo(() => {
    if (!agenteSelecionado) return [];
    return chamadas.filter((c) => c.agente === agenteSelecionado);
  }, [agenteSelecionado, chamadas]);

  const columns = [
    {
      title: "Data e Hora",
      dataIndex: "data",
      key: "data",
      width: 150,
      render: (text) => (
        <Space direction="vertical" size={0}>
          <Text strong>{dayjs(text).format("DD/MM/YYYY")}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(text).format("HH:mm")}</Text>
        </Space>
      ),
    },
    { title: "Fila", dataIndex: "fila", key: "fila", width: 120, render: (fila) => <Tag color="geekblue">{fila}</Tag> },
    { title: "Espera", dataIndex: "espera", key: "espera", width: 100, align: "center" },
    { title: "Duração", dataIndex: "duracao", key: "duracao", width: 100, align: "center" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status) => {
        const statusMap = {
          completed: { text: "Completada", color: "success", icon: <CheckCircleOutlined /> },
          noanswer: { text: "Sem resposta", color: "error", icon: <CloseCircleOutlined /> },
          abandoned: { text: "Abandonada", color: "warning", icon: <ExclamationCircleOutlined /> },
        };
        const config = statusMap[status] || { text: status, color: "default", icon: <InfoCircleOutlined /> };
        return <Badge status={config.color} text={<Space size={4}>{config.icon}{config.text}</Space>} />;
      },
    },
    {
      title: "Operador",
      dataIndex: "agente",
      key: "agente",
      width: 150,
      render: (agente) => (
        <Space>
          <Avatar size="small" icon={<PhoneOutlined />} style={{ backgroundColor: "#1890ff" }} />
          <Text>{agente}</Text>
        </Space>
      ),
    },
  ];

  const stats = useMemo(() => {
    const lista = [
      { title: "Total atendidas", value: agenteDetalhes?.total_atendidas || "0", color: "#1890ff", icon: <PhoneOutlined /> },
      { title: "Duração média", value: agenteDetalhes?.duracao_media_completed || "00:00:00", color: "#52c41a", icon: <CheckCircleOutlined /> },
      { title: "Duração máxima", value: agenteDetalhes?.duracao_maxima || "00:00:00", color: "#faad14", icon: <ExclamationCircleOutlined /> },
    ];
    if (datas && datas[0] && datas[1] && datas[0].isSame(datas[1], "day") &&
      (agenteDetalhes?.periodo_trabalho || agenteDetalhes?.horas_trabalhadas)) {
      lista.push(
        { title: "Período de Trabalho", value: agenteDetalhes?.periodo_trabalho || "-", color: "#722ed1", icon: <ClockCircleOutlined /> },
        { title: "Horas Trabalhadas", value: agenteDetalhes?.horas_trabalhadas || "-", color: "#eb2f96", icon: <BarChartOutlined /> }
      );
    }
    return lista;
  }, [agenteDetalhes, datas]);

  const p = isMobile ? "12px 8px" : isTablet ? "20px 16px" : "32px 48px";

  return (
    <div style={{ padding: p, minHeight: "100vh", overflowY: "auto", boxSizing: "border-box", background: "linear-gradient(145deg, #f0f2f5 0%, #f8f9fa 100%)" }}>

      {/* Cabeçalho */}
      <div style={{ marginBottom: 24, padding: isMobile ? "16px" : "24px 32px", background: "linear-gradient(135deg, rgba(24,144,255,0.08) 0%, rgba(24,144,255,0.02) 100%)", borderRadius: 20, boxShadow: "0 4px 12px rgba(0,0,0,0.03)", border: "1px solid rgba(24,144,255,0.1)" }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} lg={12}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar size={isMobile ? 48 : 64} icon={<TeamOutlined />} style={{ backgroundColor: "#1890ff", boxShadow: "0 4px 12px rgba(24,144,255,0.3)", flexShrink: 0 }} />
              <div>
                <Title level={isMobile ? 4 : 2} style={{ margin: 0, color: "#1a3353" }}>
                  {isMobile ? "Chamadas por Agentes" : "Gestão de Chamadas por Agentes"}
                </Title>
                {!isMobile && <Text type="secondary" style={{ fontSize: 16 }}>Analise o desempenho dos agentes</Text>}
              </div>
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12}>
                <Text strong style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Período</Text>
                <RangePicker
                  format="DD/MM/YYYY"
                  value={datas}
                  onChange={(values) => setDatas(values)}
                  style={{ borderRadius: 10, width: "100%" }}
                  size={isMobile ? "middle" : "large"}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Text strong style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Selecionar Agente</Text>
                <Select
                  placeholder="Selecione um agente"
                  allowClear
                  value={agenteSelecionado}
                  onChange={(value) => setAgenteSelecionado(value)}
                  style={{ width: "100%" }}
                  size={isMobile ? "middle" : "large"}
                  loading={loadingDados}
                  disabled={!datas || loadingDados}
                >
                  {dadosAgentes.map((agente, index) => (
                    <Option key={index} value={agente.agente}>{agente.agente}</Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      {/* Loading */}
      {loadingDados && (
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <Spin size="large" />
          <Text style={{ display: "block", marginTop: 16, color: "#666" }}>Carregando dados...</Text>
        </div>
      )}

      {/* Cards de resumo */}
      {agenteDetalhes && !loadingDados && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card bordered={false} style={{ borderRadius: 20, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", boxShadow: "0 8px 24px rgba(102,126,234,0.3)", overflow: "hidden" }} bodyStyle={{ padding: isMobile ? 16 : 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
                <Avatar size={isMobile ? 40 : 56} icon={<PhoneOutlined />} style={{ backgroundColor: "rgba(255,255,255,0.2)", color: "white", flexShrink: 0 }} />
                <Title level={isMobile ? 4 : 3} style={{ color: "white", margin: 0 }}>{agenteDetalhes.agente}</Title>
              </div>
              <Row gutter={[12, 12]}>
                {stats.map((item, index) => (
                  <Col xs={12} sm={8} md={6} key={index}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.1)", padding: isMobile ? "10px" : "16px", borderRadius: 16, backdropFilter: "blur(4px)" }}>
                      <div style={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 12, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48, display: "flex", alignItems: "center", justifyContent: "center", fontSize: isMobile ? 16 : 20, flexShrink: 0 }}>
                        {item.icon}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: isMobile ? 10 : 12, display: "block" }}>{item.title}</Text>
                        <div style={{ color: "white", fontSize: isMobile ? 14 : 18, fontWeight: "bold", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.value}</div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* Gráfico */}
      {agenteSelecionado && dadosGrafico.length > 0 && (
        <Card style={{ marginBottom: 24, borderRadius: 20, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
          title={<Space><BarChartOutlined style={{ color: "#1890ff" }} /><span style={{ fontWeight: 600, fontSize: isMobile ? 14 : 16 }}>Chamadas por dia — {agenteSelecionado}</span></Space>}
          bordered={false}
          bodyStyle={{ padding: isMobile ? "12px 8px" : "24px" }}>
          <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
            <BarChart data={dadosGrafico} margin={{ top: 20, right: 10, left: 0, bottom: 5 }}>
              <XAxis dataKey="data" tick={{ fontSize: isMobile ? 10 : 12 }} />
              <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />
              <RechartsTooltip contentStyle={{ backgroundColor: "#fff", borderRadius: 10, border: "1px solid #d9d9d9", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} formatter={(value) => [value, "Chamadas atendidas"]} labelFormatter={(label) => `Data: ${label}`} />
              <Legend />
              <Bar dataKey="chamadas" fill="#1890ff" name="Chamadas atendidas" barSize={isMobile ? 20 : 40} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Estados vazios */}
      {!agenteSelecionado && datas && !loadingDados && (
        <Card style={{ marginBottom: 24, borderRadius: 20, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", textAlign: "center", padding: isMobile ? "30px 16px" : "60px 20px" }} bordered={false}>
          <InfoCircleOutlined style={{ fontSize: isMobile ? 48 : 64, color: "#1890ff", opacity: 0.6 }} />
          <Title level={4} style={{ color: "#666", marginTop: 16 }}>Selecione um agente para visualizar os dados</Title>
        </Card>
      )}
      {!datas && (
        <Card style={{ marginBottom: 24, borderRadius: 20, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", textAlign: "center", padding: isMobile ? "30px 16px" : "60px 20px" }} bordered={false}>
          <InfoCircleOutlined style={{ fontSize: isMobile ? 48 : 64, color: "#1890ff", opacity: 0.6 }} />
          <Title level={4} style={{ color: "#666", marginTop: 16 }}>Selecione um período para começar</Title>
        </Card>
      )}

      {/* Tabela */}
      {agenteSelecionado && (
        <Card bordered={false} style={{ borderRadius: 20, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", overflow: "hidden" }} bodyStyle={{ padding: 0 }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
            <Space size="middle" align="center" wrap>
              <Title level={4} style={{ margin: 0, fontSize: isMobile ? 15 : 18 }}>Chamadas de {agenteSelecionado}</Title>
              <Tag color="blue" style={{ fontSize: 13, padding: "4px 12px", borderRadius: 20 }}>Total: {filteredChamadas.length}</Tag>
            </Space>
          </div>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={filteredChamadas}
            loading={loading}
            size={isMobile ? "small" : "middle"}
            scroll={{ x: "max-content" }}
            pagination={{
              pageSize: 5,
              size: isMobile ? "small" : "default",
              showSizeChanger: !isMobile,
              showQuickJumper: !isMobile,
              position: ["bottomCenter"],
              showTotal: (total, range) => isMobile ? `${range[0]}-${range[1]} / ${total}` : `${range[0]}-${range[1]} de ${total} chamadas`,
              style: { margin: "16px 0", paddingBottom: 8 },
            }}
            rowClassName={() => "custom-row-hover"}
          />
        </Card>
      )}

      <style>{`
        .custom-row-hover:hover { background-color: #f0f7ff !important; transition: background-color 0.2s; }
        @media (max-width: 767px) {
          .ant-table-pagination { justify-content: center !important; }
          .ant-picker-range { font-size: 13px !important; }
        }
      `}</style>
    </div>
  );
};

export default GestaoChamadasAgentes;